import { FilePenLine, Edit2, Briefcase } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import PhoneInput from "react-phone-input-2";
import { ApiUrl } from "../config/apiConfig";
import { IJob } from "../models/Job";
import { putData } from "../services/Axios/Axios";
import { SlideTabsRef } from "./UIElement/SlideTabs";
import UnderlineSlidingTabs from "./UIElement/UnderlineSlidingTabs";

type JobSidebarInfoProps = {
    job: IJob;
    setSelectedJob: any;
    onSuccess: () => void;
};

const JobSidebarInfo: React.FC<JobSidebarInfoProps> = ({
    job,
    setSelectedJob,
    onSuccess
}) => {

    const [currentTab, setTab] = useState("info");
    const [isEditMode, setIsEditMode] = useState(false);

    const contentGridRef = useRef<HTMLDivElement>(null);
    const slideTabsRef = useRef<SlideTabsRef>(null);

    

    useEffect(() => {
        if (slideTabsRef.current) {
            slideTabsRef.current.resetToFirstTab();
        }
    }, [job?._id]);

    const autoUpdate = async () => {
        try {
            await putData(`${ApiUrl.updateJob}${job._id}`, job);
             onSuccess();
        } catch (error) {
            console.error(error);
        }
    };

    const autoUpdateField = async (field: keyof IJob, value: any) => {
        try {
            await putData(`${ApiUrl.updateJob}${job._id}`, {
                [field]: value
            });
            await onSuccess();
        } catch (error) {
            console.error(error);
        }
    };

    const handleInputChange = <K extends keyof IJob>(
        field: K,
        value: IJob[K]
    ) => {
        setSelectedJob((prev: any) => ({
            ...prev,
            [field]: value
        }));
    };

    const tabs = [
        { label: "Information", value: "info" }
    ];

    return (
        <div className="max-w-[1400px] bg-[#FAFDFF] h-full mx-auto">

            {/* Header */}
            <div className="flex items-start justify-between pl-5 pr-2 pt-4 pb-3 border-b border-gray-100 bg-white mr-2">
                <div className="flex items-start gap-3">
                    <div className="w-[68px] h-[68px] shadow-circle hover:shadow-md  ease-in-out overflow-hidden  rounded-full flex items-center justify-center flex-shrink-0">

                        <Briefcase size={36} color="#bbf7d0" />

                    </div>
                    <div className="pt-0.5">
                        <h1 className="text-[20px] font-semibold text-gray-900 leading-tight mb-0.5">
                            {job.jobTitle}
                        </h1>
                        <p className="text-[14px] text-gray-500 leading-[1.4]">
                            {job.businessName}
                        </p>
                        <p className="text-[14px] text-gray-500 leading-[1.4]">
                            {job.location}
                        </p>
                    </div>

                </div>
            </div>

            {/* Tabs */}
            <div className="relative">
                <UnderlineSlidingTabs
                    tabs={tabs}
                    onChange={(val) => setTab(val)}
                    ref={slideTabsRef}
                />

                {currentTab === "info" && (
                    <button
                        onClick={() => {
                            setIsEditMode(!isEditMode);
                            setTimeout(() => {
                                contentGridRef.current?.focus();
                            }, 0);
                        }}
                        className="flex absolute top-[3px] w-[80px] right-[16px] items-center gap-1.5 px-3 py-1.5 text-[12.5px] text-greenBg bg-teal-50 hover:bg-teal-100 rounded-md border border-gray-300 hover:border-greenBg my-2"
                    >
                        {isEditMode ? (
                            <>
                                <FilePenLine size={16} /> View
                            </>
                        ) : (
                            <>
                                <Edit2 size={14} /> Edit
                            </>
                        )}
                    </button>
                )}
            </div>

            {/* Content */}
            {currentTab === "info" && (
                <div className="p-3 m-3  h-[74%] border-[#EEEEEE] rounded-[10px] 2xl:h-[83%] border-[1px] bg-white pb-[12rem] overflow-y-auto">

                    <div
                        ref={contentGridRef}
                        tabIndex={0}
                        onBlur={(e) => {
                            if (
                                !e.currentTarget.contains(e.relatedTarget) &&
                                isEditMode
                            ) {
                                setIsEditMode(false);
                                autoUpdate();
                            }
                        }}
                        className="grid grid-cols-3 gap-3 mb-8"
                    >

                        {/* Job Basic Info */}
                        <div className="border-r-[1px] pr-3 border-r-[#EEEEEE]">

                            <div className="flex items-center gap-2 pb-2 mb-2 border-b-[1px] border-b-[#EEEEEE]">
                                <div className="w-[18px] h-[18px] bg-greenBtext-greenBg rounded-sm flex items-center justify-center flex-shrink-0">
                                    <img src="/assets/img/list-info.png" alt="" className="personIcon" />
                                </div>
                                <h2 className="text-[14px] font-medium text-gray-900">
                                    Job & Company Info
                                </h2>
                            </div>

                            {/* Business Name */}
                            <div className="mb-4 h-[40px]">
                                <label className="text-[11px] text-gray-500 block mb-1">
                                    Business Name
                                </label>
                                {isEditMode ? (
                                    <input
                                        value={job.businessName}
                                        onChange={(e) =>
                                            handleInputChange("businessName", e.target.value)
                                        }
                                        className="font-semibold text-sm bg-white w-full border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-greenBg"
                                    />
                                ) : (
                                    <p className="text-[13.5px] text-gray-900">
                                        {job.businessName}
                                    </p>
                                )}
                            </div>

                            {/* Job Title */}
                            <div className="mb-4 h-[40px]">
                                <label className="text-[11px] text-gray-500 block mb-1">
                                    Job Title
                                </label>
                                {isEditMode ? (
                                    <input
                                        value={job.jobTitle}
                                        onChange={(e) =>
                                            handleInputChange("jobTitle", e.target.value)
                                        }
                                        className="font-semibold text-sm bg-white w-full border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-greenBg"
                                    />
                                ) : (
                                    <p className="text-[13.5px] text-gray-900">
                                        {job.jobTitle}
                                    </p>
                                )}
                            </div>

                            {/* Location */}
                            <div className="mb-4 h-[40px]">
                                <label className="text-[11px] text-gray-500 block mb-1">
                                    Location
                                </label>
                                {isEditMode ? (
                                    <input
                                        value={job.location}
                                        onChange={(e) =>
                                            handleInputChange("location", e.target.value)
                                        }
                                        className="font-semibold text-sm bg-white w-full border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-greenBg"
                                    />
                                ) : (
                                    <p className="text-[13.5px] text-gray-900">
                                        {job.location}
                                    </p>
                                )}
                            </div>

                            {/* Category */}
                            {/* <div className="mb-4 h-[40px]">
                                <label className="text-[11px] text-gray-500 block mb-1">
                                    Category
                                </label>
                                {isEditMode ? (
                                    <input
                                        value={job.categoryId}
                                        onChange={(e) => {
                                            handleInputChange("categoryId", e.target.value);
                                            autoUpdateField("categoryId", e.target.value);
                                        }}
                                        className={selectClass}
                                    />
                                ) : (
                                    <p className="text-[13.5px] text-gray-900">
                                        {job.categoryId}
                                    </p>
                                )}
                            </div> */}

                        </div>

                        {/* Contact Info */}
                        <div className="border-r-[1px] pr-3 border-r-[#EEEEEE]">

                            <div className="flex items-center gap-2 pb-2 mb-2 border-b-[1px] border-b-[#EEEEEE]">
                                <div className="w-[18px] h-[18px] bg-greenBtext-greenBg rounded-sm flex items-center justify-center flex-shrink-0">
                                    <img src="/assets/img/contact.png" alt="" className="personIcon" />
                                </div>
                                <h2 className="text-[14px] font-medium text-gray-900">
                                    Contact Details
                                </h2>
                            </div>

                            {/* Contact */}
                            <div className="mb-4 h-[40px]">
                                <label className="text-[11px] text-gray-500 block mb-1">
                                    Contact
                                </label>
                                {isEditMode ? (
                                     <PhoneInput
                                     value={job.contact}
                                     onlyCountries={["us"]}
                                     country={"us"}
                                     onChange={(v) => handleInputChange("contact",v)}
                                     disableDropdown
                                     inputClass={`w-full  px-4 py-[3px] border leading-[20px] border-gray-400 mt-[2px] hover:border-gray-600 rounded-[5px] bg-gray-50 text-gray-800 font-normal focus:outline-none focus:ring-[1.5px]  focus:ring-greenBg  mb-1 focus:border-transparent transition-all duration-300  text-[13px] overflow-hidden !h-[30px] !w-full`}
                                   />
                                ) : (
                                    <p className="text-[13.5px] text-gray-900">
                                        {job.contact}
                                    </p>
                                )}
                            </div>

                            {/* Email */}
                            <div className="mb-4 h-[40px]">
                                <label className="text-[11px] text-gray-500 block mb-1">
                                    Email
                                </label>
                                {isEditMode ? (
                                    <input
                                        type="email"
                                        value={job.email}
                                        onChange={(e) =>
                                            handleInputChange("email", e.target.value)
                                        }
                                        className="font-semibold text-sm bg-white w-full border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-greenBg"
                                    />
                                ) : (
                                    <p className="text-[13.5px] text-gray-900">
                                        {job.email}
                                    </p>
                                )}
                            </div>

                            {/* Website */}
                            <div className="mb-4 h-[40px]">
                                <label className="text-[11px] text-gray-500 block mb-1">
                                    Company Website
                                </label>
                                {isEditMode ? (
                                    <input
                                        value={job.companyWebsite}
                                        onChange={(e) =>
                                            handleInputChange("companyWebsite", e.target.value)
                                        }
                                        className="font-semibold text-sm bg-white w-full border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-greenBg"
                                    />
                                ) : (
                                    <p className="text-[13.5px] text-gray-900">
                                        {job.companyWebsite}
                                    </p>
                                )}
                            </div>

                        </div>

                        {/* Social / Apply */}
                        <div>

                            <div className="flex items-center gap-2 pb-2 mb-2 border-b-[1px] border-b-[#EEEEEE]">
                                <div className="w-[18px] h-[18px] bg-greenBtext-greenBg rounded-sm flex items-center justify-center flex-shrink-0">
                                    <img src="/assets/img/card.png" alt="" className="personIcon" />
                                </div>
                                <h2 className="text-[14px] font-medium text-gray-900">
                                    Links
                                </h2>
                            </div>

                            {/* Apply Link */}
                            <div className="mb-4">
                                <label className="text-[11px] text-gray-500 block mb-1">
                                    Apply Link
                                </label>
                                {isEditMode ? (
                                    <input
                                        value={job.applyLink}
                                        onChange={(e) =>
                                            handleInputChange("applyLink", e.target.value)
                                        }
                                        className="font-semibold text-sm bg-white w-full border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-greenBg"
                                    />
                                ) : (
                                    <p className="text-[13.5px] text-gray-900">
                                        {job.applyLink}
                                    </p>
                                )}
                            </div>

                            {/* LinkedIn */}
                            <div className="mb-4">
                                <label className="text-[11px] text-gray-500 block mb-1">
                                    LinkedIn
                                </label>
                                {isEditMode ? (
                                    <input
                                        value={job.linkedin ?? ""}
                                        onChange={(e) =>
                                            handleInputChange("linkedin", e.target.value)
                                        }
                                        className="font-semibold text-sm bg-white w-full border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-greenBg"
                                    />
                                ) : (
                                    <p className="text-[13.5px] text-gray-900">
                                        {job.linkedin ?? "N/A"}
                                    </p>
                                )}
                            </div>

                            {/* Facebook */}
                            <div className="mb-4">
                                <label className="text-[11px] text-gray-500 block mb-1">
                                    Facebook
                                </label>
                                {isEditMode ? (
                                    <input
                                        value={job.facebook ?? ""}
                                        onChange={(e) =>
                                            handleInputChange("facebook", e.target.value)
                                        }
                                        className="font-semibold text-sm bg-white w-full border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-greenBg"
                                    />
                                ) : (
                                    <p className="text-[13.5px] text-gray-900">
                                        {job.facebook ?? "N/A"}
                                    </p>
                                )}
                            </div>

                            {/* Instagram */}
                            <div className="mb-4">
                                <label className="text-[11px] text-gray-500 block mb-1">
                                    Instagram
                                </label>
                                {isEditMode ? (
                                    <input
                                        value={job.instagram ?? ""}
                                        onChange={(e) =>
                                            handleInputChange("instagram", e.target.value)
                                        }
                                        className="font-semibold text-sm bg-white w-full border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-greenBg"
                                    />
                                ) : (
                                    <p className="text-[13.5px] text-gray-900">
                                        {job.instagram ?? "N/A"}
                                    </p>
                                )}
                            </div>

                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default JobSidebarInfo;
